import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import loader from '../../assets/img/loader.svg';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  getAudiences,
  toggleAudienceDisabled,
} from '@/api/audience/audience.api';

export default function Audience() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [audiences, setAudiences] = useState([]);

  useEffect(() => {
    fetchAudiences();
  }, []);

  const fetchAudiences = async () => {
    setLoading(true);
    setAudiences(await getAudiences());
    setLoading(false);
  };

  const handleToggleDisabled = async (id) => {
    try {
      await toggleAudienceDisabled(id);
      fetchAudiences();
    } catch (error) {
      console.error('Error toggling audience status:', error);
    }
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='primary'>
            <h4>{t('audience.title')}</h4>
          </CardHeader>

          <CardBody>
            <div>
              {loading && (
                <div className='loaders'>
                  <img src={loader} alt={t('common.loading')} />
                </div>
              )}

              <div className='t-header'>
                <div className='t-header-left'>
                  <Link to={`/facility/create-audience`} className='link'>
                    <span className='add-icon'>
                      <AddIcon color='primary' />
                    </span>
                    {t('audience.createAudience')}
                  </Link>
                </div>
                <div className='t-header-right'>
                  <div className='custom-search-box'>
                    <input
                      type='search'
                      className='seacrh-field'
                      placeholder={t('audience.searchPlaceholder')}
                    />
                    <span className='search-icon'>
                      <SearchIcon />
                    </span>
                  </div>
                </div>
              </div>

              <Table width='100%' id='template'>
                <TableHead>
                  <TableRow>
                    <TableCell align='left'>
                      {t('audience.table.audienceName')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('audience.table.identifiers')}
                    </TableCell>
                    <TableCell align='center'>
                      {t('audience.table.status')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('audience.table.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>

                {audiences.length ? (
                  <TableBody>
                    {audiences.map((obj, index) => (
                      <TableRow
                        className='txtalign'
                        key={index}
                        style={{
                          backgroundColor: obj.disabled
                            ? '#f5f5f5'
                            : 'transparent',
                          opacity: obj.disabled ? 0.7 : 1,
                        }}>
                        <TableCell width='30%'>{obj.name}</TableCell>
                        <TableCell width='30%'>
                          {obj.audience_identifiers.join(', ')}
                        </TableCell>
                        <TableCell width='15%' align='center'>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!obj.is_disabled}
                                onChange={() => handleToggleDisabled(obj.id)}
                                color='primary'
                                size='small'
                              />
                            }
                            label={
                              obj.is_disabled
                                ? t('audience.status.disabled')
                                : t('audience.status.enabled')
                            }
                            labelPlacement='end'
                          />
                        </TableCell>
                        <TableCell width='15%'>
                          <Link
                            to={`/facility/edit-audience/${obj.id}`}
                            title={t('audience.updateAudience')}>
                            <EditIcon color='primary' />
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell>{t('audience.noDataFound')}</TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
